import { useCookies } from "vue3-cookies";

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
} from "firebase/auth";
import type AuthService from "@/services/auth/auth-service";
import axiosInstance from "@/services/auth/axios-auth-instance";
import { type AxiosInstance } from "axios";
import { auth, addUser } from "./auth/auth.service";
import { User } from "@/data/interfaces/UserInterface";
import { Response } from "@/data/interfaces/ResponseInterface";
import { COOKIE_USERNAME, COOKIE_TOKEN, COOKIE_DATA } from "@/utils/constants";
import usersApi from "@/api/users.api";

const { cookies } = useCookies();

class FirebaseAuthService implements AuthService {
  public constructor(
    private readonly axios: AxiosInstance,
    private readonly signIn: typeof signInWithEmailAndPassword, // Injected dependency
    private readonly signUp: typeof createUserWithEmailAndPassword // Injected dependency
  ) {}

  async login(username: string, password: string): Promise<Response> {
    try {
      const response = await usersApi.signIn({ username, password });
      const firebaseToken = response.token;
      if (firebaseToken) {
        cookies.set(COOKIE_USERNAME, response.user.username, "8h");
        cookies.set(COOKIE_TOKEN, firebaseToken, "8h");
        cookies.set(COOKIE_DATA, response.user, "8h");
      }

      return {
        username: response.user.username,
        token: firebaseToken,
        data: response.user,
      };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async register(user: User): Promise<object> {
    try {
      const response = await createUserWithEmailAndPassword(
        auth,
        user?.email,
        user?.password
      );
      addUser(response.user.uid, user.data);
      const firebaseToken = await this.getToken();
      return {
        username: response.user.email,
        token: firebaseToken,
      };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async resetPasswordWithPhoneNumber(
    phoneNumber: string,
    recaptcha: RecaptchaVerifier
  ): Promise<{ confirmationResult: ConfirmationResult }> {
    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptcha
      );
      return {
        confirmationResult,
      };
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async logout() {
    try {
      cookies.remove(COOKIE_USERNAME);
      cookies.remove(COOKIE_TOKEN);
      cookies.remove(COOKIE_DATA);
      await auth.signOut();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getToken(): Promise<string> {
    try {
      const token: string | undefined = await auth?.currentUser?.getIdToken();
      return token ?? "";
      // Use the token for further operations
    } catch (error) {
      console.error("Error occurred while retrieving the token");
      return "";
    }
  }
}

export default new FirebaseAuthService(
  axiosInstance,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
);
